import * as XLSX from 'xlsx';
import { z, ZodTypeAny } from 'zod';

function isNumberOrMixedSchema(schema: ZodTypeAny): boolean {
  const typeName = schema._def.typeName;

  // Direct matches
  if (typeName === 'ZodNumber' || typeName === 'ZodAny' || typeName === 'ZodUnion') {
    return true;
  }

  // Unwrap ZodEffects (created by .refine(), .transform(), z.preprocess())
  if (typeName === 'ZodEffects' && schema._def.schema) {
    return isNumberOrMixedSchema(schema._def.schema);
  }

  // Unwrap ZodOptional and ZodNullable
  if ((typeName === 'ZodOptional' || typeName === 'ZodNullable') && schema._def.innerType) {
    return isNumberOrMixedSchema(schema._def.innerType);
  }

  // Unwrap ZodDefault
  if (typeName === 'ZodDefault' && schema._def.innerType) {
    return isNumberOrMixedSchema(schema._def.innerType);
  }

  return false;
}

export function SafeReadCell<T>(ws: XLSX.WorkSheet, cellID: string, verify: ZodTypeAny): { value?: T, error?: string } {
  try {
    return { value: ReadCell(ws, cellID, verify) };
  } catch (e) {
    return { error: `${e}` };
  }
}

export function ReadCell<T>(ws: XLSX.WorkSheet, cellID: string, verify: ZodTypeAny): T {
  const cell = ws[cellID];
  let val: unknown;
  // if number, pull raw value from cell instead of the formatted value
  if (isNumberOrMixedSchema(verify)) {
    val = cell?.v;
  } else {
    val = cell?.w;
  }
  try {
    return verify.parse(val) as T;
  } catch (e) {
    if (e instanceof z.ZodError) {
      throw new Error(`Cell ${cellID} is invalid: ${e.errors.map(err => err.message).join(', ')}`);
    }
    throw e;
  }
}

export function IsRowEmpty(ws: XLSX.WorkSheet, row: number, columnsToCheck: string[]): boolean {
  const values = columnsToCheck.map(
    x => ReadCell(
      ws,
      `${x}${row + 1}`,
      z.string().optional())
  );
  return values.every(x => !x);
}

export type XLSXCell = {data: any, address: {c: number, r: number}};

export function readWorksheet(workbook: XLSX.WorkBook, sheetIndex: number): XLSXCell[] {
  const ws = workbook.Sheets[workbook.SheetNames[sheetIndex]];
  const range = XLSX.utils.decode_range(ws['!ref'] || '');
  const dataRange = [] as XLSXCell[];

  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell_address = { c: C, r: R };
      const data = XLSX.utils.encode_cell(cell_address);
      if (ws[data] && cell_address.r >= 0) {
        dataRange.push({ data: ws[data], address: cell_address });
      }
    }
  }
  return dataRange;
}

const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'Z', 'Y', 'Z'];

export function colLetter(index: number): string {
  if (index > letters.length - 1 || index < 0) {
    return '';
  }
  return letters[index];
}

export function convertToISODate(val: undefined | string): undefined | string {
  return !val ? undefined : new Date(val).toISOString();
}

export function yesNoToBool(val: string | undefined): boolean | undefined {
  if (!val) return undefined;

  switch (val.trim().toLowerCase()) {
    case 'no':
      return false;
    case 'yes':
      return true;
    default:
      return undefined;
  }
}
